import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PostCard = ({ post, onLike, onComment, onSave, onShare }) => {
  const [showFullContent, setShowFullContent] = useState(false);
  const [isLiked, setIsLiked] = useState(post.isLiked);
  const [isSaved, setIsSaved] = useState(post.isSaved);
  const [likesCount, setLikesCount] = useState(post.likesCount);

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    onLike(post.id, !isLiked);
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
    onSave(post.id, !isSaved);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Gift Wins': 'bg-success-100 text-success-700',
      'Need Advice': 'bg-warning-100 text-warning-700',
      'Inspiration Boards': 'bg-secondary-100 text-secondary-700',
      'Thank You Moments': 'bg-primary-100 text-primary-700'
    };
    return colors[category] || 'bg-accent-100 text-accent-700';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden hover:shadow-warm transition-all duration-300">
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Image
                src={post.author.avatar}
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              {post.author.isVerified && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Check" size={12} color="white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-semibold text-accent-800">{post.author.name}</h4>
                {post.author.badge && (
                  <span className="px-2 py-1 bg-accent-100 text-accent-600 text-xs rounded-full font-medium">
                    {post.author.badge}
                  </span>
                )}
              </div>
              <p className="text-sm text-accent-500">{formatTimeAgo(post.timestamp)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
              {post.category}
            </span>
            <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <h3 className="font-semibold text-lg text-accent-800 mb-2">{post.title}</h3>
        <div className="text-accent-600 leading-relaxed">
          {showFullContent ? (
            <p>{post.content}</p>
          ) : (
            <p>
              {post.content.length > 200 ? `${post.content.substring(0, 200)}...` : post.content}
            </p>
          )}
          {post.content.length > 200 && (
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="text-primary font-medium text-sm mt-1 hover:text-primary-600 transition-colors"
            >
              {showFullContent ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="text-secondary-600 text-sm hover:text-secondary-700 cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className="px-4 pb-3">
          {post.images.length === 1 ? (
            <div className="rounded-xl overflow-hidden">
              <Image
                src={post.images[0]}
                alt="Post image"
                className="w-full h-64 object-cover"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {post.images.slice(0, 4).map((image, index) => (
                <div key={index} className="relative rounded-lg overflow-hidden">
                  <Image
                    src={image}
                    alt={`Post image ${index + 1}`}
                    className="w-full h-32 object-cover"
                  />
                  {index === 3 && post.images.length > 4 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-semibold">+{post.images.length - 4}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gift Details */}
      {post.giftDetails && (
        <div className="px-4 pb-3">
          <div className="bg-surface rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-accent-800">Gift Details</h4>
              <span className="text-sm text-accent-500">{post.giftDetails.occasion}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-accent-500">Recipient:</span>
                <p className="font-medium text-accent-700">{post.giftDetails.recipient}</p>
              </div>
              <div>
                <span className="text-accent-500">Budget:</span>
                <p className="font-medium text-accent-700">{post.giftDetails.budget}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-3 border-t border-accent-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-2 transition-colors ${
                isLiked ? 'text-primary' : 'text-accent-500 hover:text-primary'
              }`}
            >
              <Icon name={isLiked ? "Heart" : "Heart"} size={20} className={isLiked ? 'fill-current' : ''} />
              <span className="text-sm font-medium">{likesCount}</span>
            </button>
            <button
              onClick={() => onComment(post.id)}
              className="flex items-center space-x-2 text-accent-500 hover:text-accent-700 transition-colors"
            >
              <Icon name="MessageCircle" size={20} />
              <span className="text-sm font-medium">{post.commentsCount}</span>
            </button>
            <button
              onClick={() => onShare(post.id)}
              className="flex items-center space-x-2 text-accent-500 hover:text-accent-700 transition-colors"
            >
              <Icon name="Share2" size={20} />
              <span className="text-sm font-medium">Share</span>
            </button>
          </div>
          <button
            onClick={handleSave}
            className={`transition-colors ${
              isSaved ? 'text-secondary' : 'text-accent-500 hover:text-secondary'
            }`}
          >
            <Icon name={isSaved ? "Bookmark" : "Bookmark"} size={20} className={isSaved ? 'fill-current' : ''} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostCard;